// French display formats of the interface (RG-17, RNF-24).
//
// The numeric formats come from @horizon/coeur-calcul so the screens, the PDF
// report and the Edge Functions all render the exact same figures. This module
// adds the date format and the status wordings, which are presentation only and
// therefore have no place in the calculation engine.
import { fmtEuros, fmtKE, fmtNombre, fmtPct, fmtPoints } from '@horizon/coeur-calcul';
import type {
  CodeScenario,
  RoleUtilisateur,
  StatutEcheance,
  StatutKyc,
  StatutSimulation,
  TypeEcheance,
} from '@horizon/types';

export { fmtEuros, fmtKE, fmtNombre, fmtPct, fmtPoints };

/** Minus sign U+2212, the design pack glyph for an absent value. */
const TIRET_ABSENT = '−';

/** Badge tones exposed by HzBadge. */
export type TonBadge = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/** Parse an ISO date ("2026-03-12") or an ISO timestamp into a local Date. */
function versDate(valeur: string | Date): Date {
  if (valeur instanceof Date) {
    return valeur;
  }
  // A bare date is parsed as UTC by the specification, which shifts the day
  // backwards west of Greenwich. Anchoring it at local midnight avoids that.
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(valeur) ? `${valeur}T00:00:00` : valeur);
}

/** Date in the French JJ/MM/AAAA form. Absent or invalid values render as U+2212. */
export function dateFr(valeur: string | Date | null | undefined): string {
  if (valeur === null || valeur === undefined || valeur === '') {
    return TIRET_ABSENT;
  }
  const date = versDate(valeur);
  if (Number.isNaN(date.getTime())) {
    return TIRET_ABSENT;
  }
  const jour = String(date.getDate()).padStart(2, '0');
  const mois = String(date.getMonth() + 1).padStart(2, '0');
  return `${jour}/${mois}/${date.getFullYear()}`;
}

/** Date and time in the French JJ/MM/AAAA HH:MM form, for the notification list. */
export function dateHeureFr(valeur: string | Date | null | undefined): string {
  if (valeur === null || valeur === undefined || valeur === '') {
    return TIRET_ABSENT;
  }
  const date = versDate(valeur);
  if (Number.isNaN(date.getTime())) {
    return TIRET_ABSENT;
  }
  const heures = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${dateFr(date)} ${heures}:${minutes}`;
}

/** Whole years elapsed since a birth date, or null when the date is unusable. */
export function ageEnAnnees(dateNaissance: string | null | undefined): number | null {
  if (dateNaissance === null || dateNaissance === undefined || dateNaissance === '') {
    return null;
  }
  const naissance = versDate(dateNaissance);
  if (Number.isNaN(naissance.getTime())) {
    return null;
  }
  const maintenant = new Date();
  let age = maintenant.getFullYear() - naissance.getFullYear();
  const moisEcart = maintenant.getMonth() - naissance.getMonth();
  if (moisEcart < 0 || (moisEcart === 0 && maintenant.getDate() < naissance.getDate())) {
    age -= 1;
  }
  return age;
}

export const LIBELLE_STATUT_ECHEANCE: Readonly<Record<StatutEcheance, string>> = {
  planifie: 'Planifié',
  en_cours: 'En cours',
  valide: 'Validé',
  rejete: 'Rejeté',
  annule: 'Annulé',
};

export const TON_STATUT_ECHEANCE: Readonly<Record<StatutEcheance, TonBadge>> = {
  planifie: 'info',
  en_cours: 'warning',
  valide: 'success',
  rejete: 'danger',
  annule: 'neutral',
};

export const LIBELLE_TYPE_ECHEANCE: Readonly<Record<TypeEcheance, string>> = {
  versement_initial: 'Versement initial',
  versement_mensuel: 'Versement mensuel',
  versement_exceptionnel: 'Versement exceptionnel',
  arbitrage: 'Arbitrage',
};

export const LIBELLE_STATUT_KYC: Readonly<Record<StatutKyc, string>> = {
  a_jour: 'KYC à jour',
  a_renouveler: 'KYC à renouveler',
  expire: 'KYC expiré',
};

export const TON_STATUT_KYC: Readonly<Record<StatutKyc, TonBadge>> = {
  a_jour: 'success',
  a_renouveler: 'warning',
  expire: 'danger',
};

export const LIBELLE_STATUT_SIMULATION: Readonly<Record<StatutSimulation, string>> = {
  brouillon: 'Brouillon',
  active: 'Active',
  archivee: 'Archivée',
};

export const LIBELLE_ROLE: Readonly<Record<RoleUtilisateur, string>> = {
  conseiller: 'Conseiller',
  administrateur: 'Administrateur',
  conformite: 'Conformité',
  direction: 'Direction',
};

export const LIBELLE_CLASSE_ACTIF: Readonly<Record<string, string>> = {
  actions: 'Actions',
  obligations: 'Obligations',
  fonds_euros: 'Fonds €',
  liquidites: 'Liquidités',
};

/** Label of an échéance status, safe against an unknown value coming from the API. */
export function libelleStatutEcheance(statut: StatutEcheance): string {
  return LIBELLE_STATUT_ECHEANCE[statut] ?? TIRET_ABSENT;
}

/** Badge tone of an échéance status. Colour is never the only carrier of meaning. */
export function tonStatutEcheance(statut: StatutEcheance): TonBadge {
  return TON_STATUT_ECHEANCE[statut] ?? 'neutral';
}

export interface ApiFormatsFr {
  readonly fmtEuros: typeof fmtEuros;
  readonly fmtKE: typeof fmtKE;
  readonly fmtNombre: typeof fmtNombre;
  readonly fmtPct: typeof fmtPct;
  readonly fmtPoints: typeof fmtPoints;
  readonly dateFr: typeof dateFr;
  readonly dateHeureFr: typeof dateHeureFr;
  readonly ageEnAnnees: typeof ageEnAnnees;
  readonly libelleStatutEcheance: typeof libelleStatutEcheance;
  readonly tonStatutEcheance: typeof tonStatutEcheance;
  readonly libelleTypeEcheance: Readonly<Record<TypeEcheance, string>>;
  readonly libelleStatutKyc: Readonly<Record<StatutKyc, string>>;
  readonly tonStatutKyc: Readonly<Record<StatutKyc, TonBadge>>;
  readonly libelleStatutSimulation: Readonly<Record<StatutSimulation, string>>;
  readonly libelleRole: Readonly<Record<RoleUtilisateur, string>>;
  readonly libelleClasseActif: Readonly<Record<string, string>>;
  readonly tiretAbsent: string;
}

/** Single entry point used by the screens, so no format is reimplemented locally. */
export function useFormatsFr(): ApiFormatsFr {
  return {
    fmtEuros,
    fmtKE,
    fmtNombre,
    fmtPct,
    fmtPoints,
    dateFr,
    dateHeureFr,
    ageEnAnnees,
    libelleStatutEcheance,
    tonStatutEcheance,
    libelleTypeEcheance: LIBELLE_TYPE_ECHEANCE,
    libelleStatutKyc: LIBELLE_STATUT_KYC,
    tonStatutKyc: TON_STATUT_KYC,
    libelleStatutSimulation: LIBELLE_STATUT_SIMULATION,
    libelleRole: LIBELLE_ROLE,
    libelleClasseActif: LIBELLE_CLASSE_ACTIF,
    tiretAbsent: TIRET_ABSENT,
  };
}

/** Ordered scenario labels, re-exported for the segmented control. */
export const ORDRE_SCENARIOS: readonly CodeScenario[] = ['prudent', 'equilibre', 'dynamique'];
